import { useEffect, useImperativeHandle, useMemo, useRef } from 'react';

import { Table as KitchenTable, type TypeTableOverlayRenderer, type TypeTableRef } from '@thefarmersfront/kitchen';

import clsx from 'clsx';

import { NoRowsStatusType } from '~/constants';

import { NoRowsOverlay } from '../NoRowsOverlay';
import { TableRowDimContext, TableSelectionContext, enhanceColumns } from './tableColumns';
import useTableSelection from './useTableSelection';

import type { TypeTableProps } from './table.types';

import styles from './Table.module.scss';

/** 행 높이. 지정하지 않으면 kitchen 은 콘텐츠 높이만큼 행을 늘린다. */
const ROW_HEIGHT = 40;

/**
 * kitchen Table 공통 래퍼.
 *
 * - 셀을 지정하지 않은 컬럼은 빈 값을 '-' 로 보이고, 너비·최소 너비 기본값을 채운다.
 * - selection 을 넘기면 meta.selection 컬럼(없으면 앞에 붙인 선택 컬럼) 칸 안에 체크박스를 그린다.
 *   행을 눌러 선택하지는 않는다.
 * - 목록이 비면 rowStatusType 에 맞는 안내를 보인다. 로딩·에러는 kitchen 기본 UI 를 쓴다.
 * - 새 목록이 오면 본문을 맨 위로 올린다(keepScrollOnDataChange 로 끈다).
 */
const Table = <TData,>({
  data,
  columns,
  rowStatusType,
  selection,
  getRowId,
  autoHeight = false,
  keepScrollOnDataChange = false,
  isRowDimmed,
  height,
  maxHeight,
  rowHeight = ROW_HEIGHT,
  className,
  tableRef,
  overlayRenderer,
  loading,
  error,
  ...rest
}: TypeTableProps<TData>) => {
  const rows = useMemo(() => data ?? [], [data]);
  const selectionApi = useTableSelection({ rows, getRowId, selection });
  const hasSelection = selectionApi !== null;

  const hasRowDim = isRowDimmed !== undefined;
  const rowDim = useMemo(() => (isRowDimmed ? { isRowDimmed } : undefined), [isRowDimmed]);
  const enhancedColumns = useMemo(
    () => enhanceColumns(columns, { hasSelection, hasRowDim }),
    [columns, hasSelection, hasRowDim]
  );

  const kitchenTableRef = useRef<TypeTableRef>(null);
  useImperativeHandle(
    tableRef,
    () => ({ scrollToIndex: (index, options) => kitchenTableRef.current?.scrollToIndex(index, options) }),
    []
  );
  useEffect(() => {
    if (!keepScrollOnDataChange) kitchenTableRef.current?.scrollToIndex(0, { align: 'start' });
  }, [rows, keepScrollOnDataChange]);

  // 높이를 정하지 않은 표는 부모 높이를 채운다. 높이는 화면 스타일이 덮어쓸 수 있게 CSS 로 준다.
  const isFillHeight = height === undefined && maxHeight === undefined && !autoHeight;
  const statusType = rowStatusType ?? (data === null ? NoRowsStatusType.INIT : NoRowsStatusType.EMPTY);
  const overlays = useMemo<TypeTableOverlayRenderer<TData>[]>(() => {
    if (overlayRenderer) return overlayRenderer;
    // overlayRenderer 가 매칭되면 loading/error 를 덮어쓰므로, 이때는 kitchen 기본 UI 에 맡긴다.
    if (loading || error) return [];
    return [
      {
        when: (items) => items.length === 0,
        render: (
          <div className={styles['overlay-viewport']}>
            <NoRowsOverlay rowStatusType={statusType} />
          </div>
        ),
      },
    ];
  }, [overlayRenderer, loading, error, statusType]);

  return (
    <TableSelectionContext.Provider value={selectionApi}>
      <TableRowDimContext.Provider value={rowDim}>
        <KitchenTable<TData>
          ref={kitchenTableRef}
          className={clsx(
            styles['table'],
            isFillHeight && styles['fill-height'],
            autoHeight && styles['auto-height'],
            className
          )}
          data={rows}
          columns={enhancedColumns}
          getRowId={getRowId}
          height={height}
          maxHeight={maxHeight}
          rowHeight={rowHeight}
          enableClickRowSelection={false}
          loading={loading}
          error={error}
          overlayRenderer={overlays}
          {...rest}
        />
      </TableRowDimContext.Provider>
    </TableSelectionContext.Provider>
  );
};

export default Table;
